import { useCallback, useEffect, useReducer } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useOutletContext } from 'react-router-dom';

import { useAppContext } from '@/provider/contexts';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import NoStoreSelectionBlock from '@/components/store/NoStoreSelectionBlock';

import StoreSettingsForm from './StoreSettingsForm';

import { fetchStoreDetailAPI } from '@/api/stores';
import { setSettingsAPI } from '@/api/storeManagement/settings';
import { StoreSettingsAction, StoreSettingsState } from '@/types/pages/storeManagement/settings';
import { StoreDetailResponse, StoreSettings } from '@/types/pages/stores';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';
import { join, orderBy, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { fetchAssetListAPI } from '@/api/admin/setting';
import { APIResponseModel } from '@/types';
import { AxiosResponse } from 'axios';

function reducer(state: StoreSettingsState, action: StoreSettingsAction) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_SETTINGS':
            return { ...state, settings: action.payload };
        case 'SET_STORE_INFO':
            return { ...state, storeInfo: action.payload };
        case 'SET_ORIGINAL_SETTINGS':
            return {
                ...state,
                originalSettings: action.payload,
            };
        case 'SET_CUSTOM_FIELD_OPTION':
            return { ...state, customFieldOptions: orderBy(action.payload, '') };
        case 'SET_PRIORITIZE_OPTIONS':
            return { ...state, priorityOptions: action.payload };
        case 'SET_VALIDATION_ERRORS':
            return { ...state, validationErrors: action.payload };
        case 'SET_STORE_ASSET_LIST':
            return { ...state, assetList: action.payload };
        default:
            return state;
    }
}

export default function StoreSettingsPage() {
    const { t } = useTranslation('common');
    const defaultState: StoreSettingsState = {
        isLoading: true,
        storeInfo: {},
        settings: {},
        originalSettings: {},
        customFieldOptions: [],
        priorityOptions: [],
        validationErrors: [],
        assetList: [],
        storeId: null,
    };

    const [
        {
            isLoading,
            storeInfo,
            assetList,
            settings,
            originalSettings,
            validationErrors,
            customFieldOptions,
            priorityOptions,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);

    const { manageStoreId, setNotification } = useAppContext();
    const { isStoreReady } = useOutletContext<StoreMgmtOutletContext>();

    const fetchStoreSettings = useCallback(async () => {
        const initStoreRes = (storeRes: AxiosResponse<APIResponseModel<StoreDetailResponse>>) => {
            const {
                data: { data, isSuccess, message },
            } = storeRes;
            if (isSuccess) {
                const { store_settings, layout_data_fields, rack_config, store_info } = data;
                const storeSetting = {
                    ...store_settings,
                    pp2w_trigger_fields: store_settings.pp2w_trigger_fields
                        ? store_settings.pp2w_trigger_fields.split('|').map((o) => Number(o))
                        : [],
                };
                const options = uniq([
                    ...rack_config.available_prioritize_type,
                    store_settings.planogram_priority_type,
                ]);
                dispatch({ type: 'SET_STORE_INFO', payload: store_info });
                dispatch({ type: 'SET_SETTINGS', payload: storeSetting });
                dispatch({ type: 'SET_ORIGINAL_SETTINGS', payload: storeSetting });
                dispatch({ type: 'SET_PRIORITIZE_OPTIONS', payload: options });
                dispatch({ type: 'SET_CUSTOM_FIELD_OPTION', payload: orderBy(layout_data_fields, 'name') });
                return;
            }
            setNotification({ message: message, severity: 'error' });
        };
        const initStoreAssetRes = (assetRes: AxiosResponse<APIResponseModel<string[]>>) => {
            const {
                data: { data, isSuccess },
            } = assetRes;

            if (isSuccess) {
                dispatch({ type: 'SET_STORE_ASSET_LIST', payload: data });
                return;
            }
        };
        // isStoreReady (rather than raw manageStoreId) avoids firing this
        // fetch during a known-invalid/erroring store state; the layout's
        // own store-detail fetch covers general validation, this page
        // additionally needs layout_data_fields/rack_config which aren't
        // part of the shared context, so it still fetches store details
        // itself alongside the asset list.
        if (!isStoreReady) return;
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const [storeRes, assetRes] = await Promise.all([
                fetchStoreDetailAPI(manageStoreId as number),
                fetchAssetListAPI(manageStoreId as number),
            ]);
            initStoreRes(storeRes);
            initStoreAssetRes(assetRes);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setNotification({ message: err.message || t('Failed to load settings'), severity: 'error' });
                return;
            }
            console.error('Error fetching store settings:', err);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [manageStoreId, isStoreReady]);

    useEffect(() => {
        fetchStoreSettings();
    }, [fetchStoreSettings]);

    const submitHandler = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const diff: Partial<
                StoreSettings & {
                    pp2w_trigger_fields?: number[];
                }
            > = Object.fromEntries(
                (Object.keys(settings) as Array<keyof StoreSettings>)
                    .filter((k) => settings[k] !== originalSettings[k])
                    .map((k) => [k, settings[k]]),
            );
            const payload: Partial<
                StoreSettings & {
                    pp2w_trigger_fields?: string;
                }
            > = {
                ...diff,
                pp2w_trigger_fields: join(diff.pp2w_trigger_fields, '|'),
            };
            if (!diff.pp2w_trigger_fields) {
                delete payload['pp2w_trigger_fields'];
            }

            if (Object.keys(payload).length > 0) {
                const {
                    data: { data, isSuccess, message },
                } = await setSettingsAPI(manageStoreId as number, payload);

                if (!isSuccess) {
                    dispatch({ type: 'SET_VALIDATION_ERRORS', payload: data.exception_settings || [] });
                    setNotification({ message, severity: 'error' });
                } else {
                    setNotification({ message: t('Settings saved successfully!'), severity: 'success' });
                    await fetchStoreSettings();
                }
            } else {
                setNotification({ message: t('No changes to save.'), severity: 'info' });
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                setNotification({ message: err.message || t('Failed to save settings'), severity: 'error' });
                return;
            }
            console.error('Error saving settings:', err);
        }

        dispatch({ type: 'SET_LOADING', payload: false });
    };

    const cancelHandler = () => {
        dispatch({ type: 'SET_SETTINGS', payload: originalSettings });
        dispatch({ type: 'SET_VALIDATION_ERRORS', payload: [] });
    };

    return (
        <Box>
            <BasePaper isFullHeight={false}>
                <BaseTitle icon={<Settings />} title={t('Store Settings')} />

                {!manageStoreId ? (
                    <NoStoreSelectionBlock />
                ) : isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <StoreSettingsForm
                        storeInfo={storeInfo}
                        assetList={assetList}
                        settings={settings}
                        priorityOptions={priorityOptions}
                        customFieldOptions={customFieldOptions}
                        originalSettings={originalSettings}
                        validatedResultList={validationErrors}
                        setSettings={(payload) => dispatch({ type: 'SET_SETTINGS', payload })}
                        onSubmit={submitHandler}
                        onCancel={cancelHandler}
                        isLoading={isLoading}
                    />
                )}
            </BasePaper>
        </Box>
    );
}
